import Cookies from 'js-cookie'
import { getLangNameFromCode } from 'language-name-map'
import { useRouter } from 'next/router'
import { createContext, ReactNode, useContext } from 'react'

export type AvailableLanguages = 'en' | 'pt' | 'es' | 'de' | 'it' | 'fr'

type LanguageSelectionContextType = {
  currentLocale: AvailableLanguages
  changeLanguage: (locale: string) => Promise<void>
  availableLocales: string[]
  getLangNameFromCode: typeof getLangNameFromCode
}

const LanguageSelectionContext = createContext<
  LanguageSelectionContextType | undefined
>(undefined)

export function LanguageSelectionProvider({
  children,
}: {
  children: ReactNode
}) {
  const router = useRouter()

  const changeLanguage = async (locale: string) => {
    try {
      Cookies.set('NEXT_LOCALE', locale, { expires: 365 })
    } catch {
      // ignore
    }
    await router.push(router.pathname, router.asPath, { locale })
    router.reload()
  }

  const value = {
    currentLocale: (router.locale || 'en') as AvailableLanguages,
    changeLanguage,
    availableLocales: router.locales || [],
    getLangNameFromCode,
  }

  return (
    <LanguageSelectionContext.Provider value={value}>
      {children}
    </LanguageSelectionContext.Provider>
  )
}

export function useLanguageSelection() {
  const context = useContext(LanguageSelectionContext)

  if (context === undefined) {
    throw new Error(
      'useLanguageSelection must be used within a LanguageSelectionProvider'
    )
  }

  return context
}
